import { WModalButton } from "@/components/button/WToolButton.tsx";
import { EditableButton } from "@/components/forms/EditableButton.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { FormMaxWidthWrapper } from "@/components/forms/FormMaxWidthWrapper.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { getSizeParams } from "@/modules/compute/commonViewModel.ts";
import type { VirtualMachineWithAssigned } from "@/modules/compute/joinAssignedQuery.ts";
import { MachineSizeInfo } from "@/modules/compute/MachineSizeInfo.tsx";
import { VmStopModalHeaderBlock, type VmCommonFields } from "@/modules/compute/ModalHeaderBlock.tsx";
import { extractOsFields } from "@/modules/compute/os/os.ts";
import { getPoolsForLocation } from "@/modules/compute/pool/pool.ts";
import { useSizeSelect } from "@/modules/compute/useSizeSelect.ts";
import { getPrimaryStorage } from "@/modules/compute/vmEntityUtils.ts";
import { resizeVmMutation, type VmResizeBody } from "@/modules/compute/vmQuery.ts";
import { VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { getDefaultHostPool } from "@/modules/pools/logic.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";

export function VmResizeToolbarButton(props: VmResizeModalProps) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton label={t`Resize`} icon="jp-expand-2-icon" />}>
            <VmResizeModalContent {...props} />
        </WModal>
    );
}

export function VmResizeInfoButton(props: VmResizeModalProps) {
    return (
        <WModal
            button={
                <EditableButton action={undefined}>
                    <MachineSizeInfo vm={props.vm} />
                </EditableButton>
            }
        >
            <VmResizeModalContent {...props} />
        </WModal>
    );
}

export interface VmResizeModalProps {
    vm: VirtualMachineWithAssigned;
}

export function VmResizeModalContent(props: VmResizeModalProps) {
    //#region Hooks
    const mutation = useStandardMutation(resizeVmMutation);
    const { vm } = props;
    //#endregion

    return (
        <BaseResizeModal
            {...props}
            onResize={async (body) => {
                await mutation.mutateAsync({ vm, body, location: vm.location });
            }}
        />
    );
}

interface BaseResizeModalProps {
    vm: VmCommonFields;
    onResize: (body: VmResizeBody) => Promise<void>;
}

export function BaseResizeModal({ vm, onResize }: BaseResizeModalProps) {
    const { t } = useLingui();
    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);

    const { vcpu, memory, status, uuid, designated_pool_uuid } = vm;
    const primary = getPrimaryStorage(vm);

    const pool = getDefaultHostPool(getPoolsForLocation(pools, vm.location), designated_pool_uuid);

    const sizeParams = getSizeParams(pool?.guest_limits);

    //#region Hooks
    const primarySize = primary?.size ?? 0;
    const { size, props } = useSizeSelect(
        {
            disks: primarySize,
            vcpu,
            ram: memory,
            isCustom: true,
        },
        extractOsFields(vm),
        sizeParams,
    );

    //#endregion

    async function onSubmit() {
        await onResize({
            vm: {
                uuid,
                ram: size.ram,
                vcpu: size.vcpu,
            },
            disk: primary
                ? {
                      uuid,
                      size_gb: size.disks,
                      disk_uuid: primary.uuid,
                  }
                : undefined,
        });
    }

    return (
        <WModalContent
            title={t`Resize`}
            label={t`Resize`}
            isActionDisabled={status === "running"}
            modalAction={onSubmit}
            footerNotice={
                status === "running" ? t`Resizing is only possible when the virtual machine is stopped.` : undefined
            }
        >
            <VmStopModalHeaderBlock vm={vm} showStopButton />

            <FormMaxWidthWrapper>
                <FormField wide label={t`Size`} block isRequired>
                    <RadioGroupDiv>
                        <VmSizeSelect vmData={vm} sizeParams={sizeParams} {...props} />
                    </RadioGroupDiv>
                </FormField>
            </FormMaxWidthWrapper>

            <p>
                <Trans>Disk size can only be increased. This action is not reversible. Minimum disk size:</Trans>{" "}
                <b>
                    {primarySize} <Trans>GB</Trans>
                </b>
                .
            </p>
        </WModalContent>
    );
}
